import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Play, CheckCircle, AlertCircle, Loader2, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WatchTrainingPage() {
  const { linkToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [videoData, setVideoData] = useState(null);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [watchTime, setWatchTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [displayProgress, setDisplayProgress] = useState(0);
  const startTimeRef = useRef(null);
  const playerRef = useRef(null);
  const iframeRef = useRef(null);
  const lastServerUpdateRef = useRef(0);

  // Fetch video data
  useEffect(() => {
    const fetchVideo = async () => {
      try {
        const res = await axios.get(`${API}/watch/${linkToken}`);
        setVideoData(res.data);
        // Load existing progress
        const existingProgress = res.data.progress || 0;
        setProgress(existingProgress);
        setDisplayProgress(existingProgress);
        lastServerUpdateRef.current = existingProgress;
        if (existingProgress >= 100) {
          setCompleted(true);
        }
      } catch (e) {
        setError(e.response?.data?.detail || 'Invalid or expired training link');
      } finally {
        setLoading(false);
      }
    };
    
    if (linkToken) {
      fetchVideo();
    }
  }, [linkToken]);

  // Update progress to server - use sendBeacon for reliability on mobile
  const updateProgress = useCallback(async (newProgress, useBeacon = false) => {
    if (completed) return;
    
    const roundedProgress = Math.min(Math.round(newProgress), 100);
    if (roundedProgress <= progress) return;
    
    try {
      // Use sendBeacon for page unload - more reliable on mobile Safari
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/watch/${linkToken}/progress?progress=${roundedProgress}`);
        setProgress(roundedProgress);
      } else {
        await axios.post(`${API}/watch/${linkToken}/progress?progress=${roundedProgress}`);
        setProgress(roundedProgress);
      }
      
      if (roundedProgress >= 100 && !completed) {
        setCompleted(true);
      }
    } catch (e) {
      console.error('Failed to update progress:', e);
      // Fallback to sendBeacon if axios fails
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/watch/${linkToken}/progress?progress=${roundedProgress}`);
      }
    }
  }, [linkToken, progress, completed]);

  // Time-based progress tracking - ONLY counts when video is PLAYING
  useEffect(() => {
    if (!videoData || completed) return;

    // Video duration from training link (set when admin sends training)
    const videoDuration = videoData.duration_seconds || videoData.video?.duration_seconds || 120;
    
    console.log('[Training] Video duration for progress:', videoDuration, 'seconds', '| isPlaying:', isPlaying);
    
    // Only count time when video is actually playing
    if (!isPlaying) {
      console.log('[Training] Video paused - not counting progress');
      return;
    }
    
    // Track actual watch time only when playing
    const interval = setInterval(() => {
      setWatchTime(currentWatchTime => {
        const newTime = currentWatchTime + 1;
        console.log('[Training] Watch time:', newTime, 'seconds');
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [videoData, completed, isPlaying]);

  // Calculate and update displayProgress based on watchTime
  useEffect(() => {
    if (!videoData || completed) return;
    
    // IMPORTANT: duration_seconds is at root level of API response, NOT inside video object
    const videoDuration = videoData.duration_seconds || videoData.video?.duration_seconds || 120;
    const newProgress = Math.min((watchTime / videoDuration) * 100, 100);
    
    setDisplayProgress(newProgress);
    
    // Update server every 5% progress increment
    const currentBucket = Math.floor(newProgress / 5);
    const lastBucket = Math.floor(lastServerUpdateRef.current / 5);
    
    if (currentBucket > lastBucket && currentBucket > 0) {
      lastServerUpdateRef.current = newProgress;
      // Allow full 100% when video is fully watched
      updateProgress(Math.round(newProgress));
    }
  }, [watchTime, videoData, completed, updateProgress]);

  // Track when user starts watching (visibility) and save on page leave
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // User left the page - SAVE PROGRESS IMMEDIATELY and pause tracking
        setIsPlaying(false);
        const currentProgress = Math.max(displayProgress, progress);
        if (currentProgress > 0) {
          // Use sendBeacon for reliable delivery when page is closing
          if (navigator.sendBeacon) {
            navigator.sendBeacon(`${API}/watch/${linkToken}/progress?progress=${Math.round(currentProgress)}`);
          }
        }
      }
      // NOTE: When user comes back, we do NOT auto-resume - they need to click play
    };

    // Save progress when page unloads (closing browser/tab)
    const handleBeforeUnload = () => {
      const currentProgress = Math.max(displayProgress, progress);
      if (currentProgress > 0 && navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/watch/${linkToken}/progress?progress=${Math.round(currentProgress)}`);
      }
    };

    // Also save on pagehide (more reliable on mobile Safari)
    const handlePageHide = () => {
      setIsPlaying(false); // Stop tracking
      const currentProgress = Math.max(displayProgress, progress);
      if (currentProgress > 0 && navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/watch/${linkToken}/progress?progress=${Math.round(currentProgress)}`);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);
    
    // DO NOT auto-start tracking - wait for video to actually play
    // setIsPlaying(false) is already the default state
    startTimeRef.current = Date.now();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [linkToken, displayProgress, progress]);

  // Manual mark as watched button - saves ACTUAL progress, doesn't fake 100%
  const markAsWatched = async () => {
    // Only save the actual progress they've watched
    const actualProgress = Math.max(displayProgress, progress);
    if (actualProgress < 10) {
      // If less than 10%, don't allow marking as watched
      alert('Please watch more of the video before marking as complete.');
      return;
    }
    await updateProgress(Math.round(actualProgress));
    setCompleted(true);
  };

  // YouTube Player API
  useEffect(() => {
    if (!videoData?.video?.url) return;

    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    window.onYouTubeIframeAPIReady = () => {
      const videoId = getYouTubeId(videoData.video.url);
      if (!videoId || !iframeRef.current) return;

      playerRef.current = new window.YT.Player(iframeRef.current, {
        videoId: videoId,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1
        },
        events: {
          onStateChange: (event) => {
            // YouTube Player States:
            // -1 = unstarted, 0 = ended, 1 = playing, 2 = paused, 3 = buffering, 5 = video cued
            console.log('[Training] YouTube state changed:', event.data);
            
            if (event.data === 1) {
              // Video is PLAYING
              console.log('[Training] Video PLAYING - starting progress tracking');
              setIsPlaying(true);
            } else if (event.data === 2 || event.data === 0 || event.data === -1) {
              // Video is PAUSED, ENDED, or UNSTARTED
              console.log('[Training] Video PAUSED/STOPPED - stopping progress tracking');
              setIsPlaying(false);
            }
            // Note: We don't mark 100% on video end (event.data === 0)
            // because mobile Safari fires this incorrectly when closing browser
          }
        }
      });
    };

    return () => {
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [videoData]);

  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-green-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading training video...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 flex items-center justify-center p-4">
        <div className="bg-gray-800 rounded-2xl p-8 max-w-md text-center border border-red-500/30">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Training Link Error</h1>
          <p className="text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  const video = videoData?.video;
  const driverName = videoData?.driver_name;
  const youtubeId = getYouTubeId(video?.url);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-green-500 p-4 shadow-lg">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-white text-lg font-bold">Leader Hub Training</h1>
          <p className="text-green-100 text-sm">Welcome, {driverName}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        {/* Completion Banner */}
        {completed && (
          <div className="mb-4 p-4 bg-green-500/20 rounded-2xl border border-green-500/30 flex items-center gap-3">
            <CheckCircle className="w-8 h-8 text-green-500 flex-shrink-0" />
            <div>
              <p className="font-bold text-green-400">Training Completed!</p>
              <p className="text-sm text-green-400/70">Thank you for watching this training video.</p>
            </div>
          </div>
        )}

        {/* Video Card */}
        <div className="bg-gray-800 rounded-2xl overflow-hidden shadow-xl border border-gray-700">
          {/* Video Title */}
          <div className="p-4 border-b border-gray-700">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                <Play className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="font-bold text-white">{video?.title}</h2>
                <p className="text-sm text-gray-400">{video?.duration || 'Training Video'}</p>
              </div>
            </div>
          </div>

          {/* Video Player */}
          <div className="aspect-video bg-black">
            {youtubeId ? (
              <div ref={iframeRef} className="w-full h-full" />
            ) : video?.url ? (
              <video
                src={video.url}
                controls
                className="w-full h-full"
                onTimeUpdate={(e) => {
                  const pct = (e.target.currentTime / e.target.duration) * 100;
                  if (pct > progress) {
                    updateProgress(pct);
                  }
                }}
                onEnded={() => updateProgress(100)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-500">
                Video not available
              </div>
            )}
          </div>

          {/* Progress Section */}
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-400">Watch Progress</span>
              <span className={`text-sm font-bold ${completed ? 'text-green-400' : 'text-white'}`}>
                {Math.round(displayProgress || progress)}%
              </span>
            </div>
            <div className="h-3 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full rounded-full transition-all duration-300 ${
                  completed ? 'bg-green-500' : (displayProgress || progress) > 0 ? 'bg-blue-500' : 'bg-gray-600'
                }`}
                style={{ width: `${displayProgress || progress}%` }}
              />
            </div>
            
            {/* Manual Complete Button */}
            {!completed && (displayProgress || progress) < 100 && (
              <button
                onClick={markAsWatched}
                className="mt-4 w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-bold flex items-center justify-center gap-2 transition-colors"
              >
                <Eye className="w-5 h-5" />
                Save Progress ({Math.round(displayProgress || progress)}%)
              </button>
            )}
          </div>

          {/* Description */}
          {video?.description && (
            <div className="p-4 border-t border-gray-700">
              <h3 className="font-medium text-white mb-2">About This Training</h3>
              <p className="text-gray-400 text-sm">{video.description}</p>
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <h3 className="font-medium text-white mb-2">Instructions</h3>
          <ul className="text-sm text-gray-400 space-y-1">
            <li>• Watch the video to track your progress</li>
            <li>• Progress updates automatically as you watch</li>
            <li>• Click "I've Watched This Video" when done</li>
          </ul>
        </div>

        {/* Hub Access Notice */}
        <div className="mt-4 bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-blue-400 text-sm">Need Hub Access?</h4>
              <p className="text-xs text-blue-400/70 mt-1">
                To access the full Leader Hub with all features, you need admin approval. 
                Contact your administrator to request login access.
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-xs pb-8">
          Leader Ultimate Hub - Training Management System
        </div>
      </div>
    </div>
  );
}
